<template>
  <div>
    <h1>Fleet Monitor</h1>

    <!-- filters panel -->
    <monitor-filters
      @search="applyFilter"
      @fleets-loaded="fleetsLoaded"
      :apiRequestData="apiRequestData"
    />

    <!-- cards rows -->
    <b-row>
      <b-col cols="6">
        <fleet-vessels-card
          class="fleet-card"
          :fleet-id="filterParameters.fleetId"
          :fleets="fleets"
        />
      </b-col>
      <b-col cols="6">
        <b-card no-body>
          <template #header
            >Port Map
            <div class="card-header-actions">
              <!-- show bubbles switch
              <custom-switch
                label="Show bubbles"
                v-model="showBubbles"
              /> -->
            </div>
          </template>

          <b-card-body body-class="p-0" class="fleet-map-card">
            <base-map
              :vessels="vessels"
              :hightlighted-vessel-imo="hightlightedVesselIMO"
              :origin-bubbles="originPortsFormatted"
              :selected-vessel-imo="selectedVesselIMO"
              :highlighted-vessel-imo="highlightedVesselIMO"
              :destination-bubbles="destinationPortsFormatted"
              :map-bounds="mapBounds"
              @vessel-popup-ready="handleVesselPopupReady"
            />
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <b-row>
      <b-col cols="6">
        <movements-stat class="fleet-card" :stats="movementsStat" />
      </b-col>
      <b-col cols="6">
        <periodic-stats
          :seasonal="true"
          class="fleet-card"
          :stats="periodicStats"
          @search="applyFilter"
        />
      </b-col>
    </b-row>

    <b-row>
      <b-col cols="6">
        <segments-chart
          class="fleet-card segments-card"
          title="Top Commodities"
          :show-percentage="true"
          :items="commodityStat"
        />
      </b-col>
      <b-col cols="6">
        <b-row>
          <b-col cols="6">
            <directions-stat
              title="Top Origins"
              class="fleet-card"
              :countries="originsStat.countries"
              :ports="originsStat.ports"
            />
          </b-col>
          <b-col cols="6">
            <directions-stat
              title="Top Destinations"
              class="fleet-card"
              :countries="destinationsStat.countries"
              :ports="destinationsStat.ports"
            />
          </b-col>
        </b-row>
      </b-col>
    </b-row>

    <b-row>
      <b-col lg="12" class="d-flex">
        <voyages
          :disabled="!filterParameters.fleetId"
          :export-types="['csv']"
          :export-loading="voyages.exportLoading"
          @export="handleVoyagesExport"
          :items="voyages.items"
          :currentPage="voyages.currentPage"
          @page-changed="voyagesPageChanged"
        />
      </b-col>
    </b-row>
  </div>
</template>

<script>
import getNested from "lodash-es/get";
import debounce from "lodash-es/debounce";
import pickBy from "lodash-es/pickBy";
import { mapActions, mapGetters } from "vuex";

import MonitorFilters from "../components/fleets/MonitorFilters";
import FleetVesselsCard from "../components/fleets/FleetVesselsCard";
import MovementsStat from "../components/tradeflows/MovementsStat";
import PeriodicStats from "../components/tradeflows/PeriodicStats";
import DirectionsStat from "../components/tradeflows/DirectionsStat";
import SegmentsChart from "../components/widgets/SegmentsChart";
import Voyages from "../components/tradeflows/Voyages";
import BaseMap from "@/base_components/BaseMap";
import { formatPorts } from "@/shared/api_formatters/ports";
import moment from "moment";
import { downloadAsFileByURL } from "@/shared/download-utils";
import { getCoordinateBounds } from "@/shared/coordinate-utils";
import { getVesselData } from "@/shared/api_formatters/vessels";

export default {
  name: "fleet-monitor",
  components: {
    BaseMap,
    SegmentsChart,
    DirectionsStat,
    PeriodicStats,
    MovementsStat,
    FleetVesselsCard,
    MonitorFilters,
    Voyages
  },
  data() {
    return {
      apiRequestData: {
        empty: {}
      },

      showBubbles: true,
      initialLoadRequestsCount: 3, // requests amount when filter is changed
      preloadRequestsCount: 1, // requests amount which will be performed on load
      preloadPagesRange: 5, // if (totalPages - currentPage) < preloadPagesRange
      // load another "preloadRequestsCount" requests

      requiredParameters: {
        // for API which requires only a subset of all filter parameters
        voyages: ["commodity", "from_date", "to_date", "imo"]
      },

      fleets: [],
      currentFleet: null,
      selectedVesselIMO: null,
      highlightedVesselIMO: null,

      filterParameters: {
        fleetId: null,
        commodity: [],
        from_date: "",
        to_date: "",
        range: "monthly"
      },

      originsStat: {
        countries: [],
        ports: []
      },
      destinationsStat: {
        countries: [],
        ports: []
      },

      vessels: [],
      selectedVesselIMO: null,
      hightlightedVesselIMO: null,

      movementsStat: {},
      periodicStats: [],
      commodityStat: [],

      voyages: {
        items: [],
        apiItemsPerPage: 50, // hardcoded for now (can be calculated by 2 requests to api and comparing items count)
        apiTotalPages: null, // or max_page, it doesn't matter since pages are starts from "1"
        currentPage: null,
        exportLoading: false
      }
    };
  },

  methods: {
    ...mapActions("api/tradeflows", [
      "getCountriesExportStat",
      "getPortsExportStat",
      "getCountriesImportStat",
      "getPortsImportStat",
      "getMovementsStat",
      "getVoyagesPeriodicStat",
      "getCommodityStat",
      "getVoyages"
    ]),

    ...mapActions("api/vessels", ["getVesselsLiveDetails"]),

    fleetsLoaded(fleets) {
      this.fleets = fleets;
    },

    applyFilter: debounce(async function(filterParameters) {
      return await this.applyFilterHandler(filterParameters);
    }, 1000),

    applyFilterHandler: async function(filterParameters) {
      this.filterParameters = {
        ...this.filterParameters,
        ...filterParameters
      };

      if (filterParameters.range) {
        await this.loadPeriodicStats(this.filterParameters);
      } else {
        if (filterParameters.fleetId) {
          const fleet = this.fleets.find(
            item => item.fleetId === filterParameters.fleetId
          );
          this.filterParameters.imo = getNested(fleet, "vessels", []).map(
            vessel => vessel.imo
          );

          await this.loadVessels(this.filterParameters);
          await this.loadTopOrigins(this.filterParameters);
          await this.loadTopDestinations(this.filterParameters);
          await this.loadMovementsStat(this.filterParameters);
          await this.loadPeriodicStats(this.filterParameters);
          await this.loadCommodityStat(this.filterParameters);
          await this.loadVoyages(this.filterParameters);
        } else {
          this.resetDashboardData();
        }
      }
    },

    resetDashboardData() {
      this.originsStat = { countries: [], ports: [] };
      this.destinationsStat = { countries: [], ports: [] };

      this.movementsStat = [];
      this.periodicStats = [];
      this.commodityStat = [];
      this.voyages = {
        items: [],
        apiItemsPerPage: 50, // hardcoded for now (can be calculated by 2 requests to api and comparing items count)
        apiTotalPages: null, // or max_page, it doesn't matter since pages are starts from "1"
        currentPage: null
      };
    },

    handleVesselPopupReady(evt) {
      evt._source.on("popupopen", evt => {
        this.selectedVesselIMO = getNested(evt, "popup.options.itemId", null);
        this.highlightedVesselIMO = getNested(
          evt,
          "popup.options.itemId",
          null
        );
      });

      evt._source.on("popupclose", () => {
        this.selectedVesselIMO = null;
        this.highlightedVesselIMO = null;
      });
    },

    async loadVessels(filterParameters) {
      let result = await this.getVesselsLiveDetails({
        imo: filterParameters.imo || []
      });
      // console.log('fleet vessels details', JSON.stringify(result, null, 2));

      if (result.error) {
        console.log("api error", result);
      } else {
        this.vessels = result.map(item => ({
          ...item,
          popupData: getVesselData(item)
        }));
      }
    },

    async loadTopOrigins(filterParameters) {
      this.originsStat.countries = await this.getCountriesExportStat(
        filterParameters
      );
      this.originsStat.ports = await this.getPortsExportStat(filterParameters);
    },

    async loadTopDestinations(filterParameters) {
      this.destinationsStat.countries = await this.getCountriesImportStat(
        filterParameters
      );
      this.destinationsStat.ports = await this.getPortsImportStat(
        filterParameters
      );
    },

    async loadMovementsStat(filterParameters) {
      this.movementsStat =
        (await this.getMovementsStat(filterParameters)) || [];
    },

    async loadPeriodicStats(filterParameters) {
      let response =
        (await this.getVoyagesPeriodicStat(filterParameters)) || {};
      this.periodicStats = response.data || [];
    },

    async loadCommodityStat(filterParameters) {
      this.commodityStat = await this.getCommodityStat(filterParameters);
    },

    async loadVoyages(filterParameters) {
      // it's possible to have a lot of pages here
      // they are loaded async to prevent interface lags

      this.voyages.items = [];
      this.voyages.currentPage = 1;

      let parameters = this.filterParametersByNames(
        filterParameters,
        this.requiredParameters.voyages
      );
      parameters.page = 1;
      await this.fetchVoyages(parameters, this.initialLoadRequestsCount);
    },

    async voyagesPageChanged(currentPage, totalPages) {
      // voyages on demand loading, overview:
      // 1. detect when a current page is less than "preloadPagesRange" value
      // 2. add items which are received by "preloadRequestsCount" requests

      if (this.getActiveRequestsCount) {
        return; // prevent multiple requests
      }

      // calculate an API page number to start
      let apiPagesLoaded =
        this.voyages.items.length / this.voyages.apiItemsPerPage;

      if (
        totalPages - currentPage < this.preloadPagesRange &&
        Number.isInteger(apiPagesLoaded)
      ) {
        let parameters = { ...this.filterParameters, page: apiPagesLoaded + 1 };

        await this.fetchVoyages(parameters, this.preloadRequestsCount);
        // b-pagination resets a current page when data is changed we have to restore it this way
        // https://github.com/bootstrap-vue/bootstrap-vue/issues/3372
        this.voyages.currentPage = currentPage;
      }
    },

    async fetchVoyages(filterParameters, requestsCount) {
      let countdown = requestsCount; // no more than countdown requests
      let parameters = { ...filterParameters };
      let response = null;

      do {
        response = (await this.getVoyages(parameters)) || {};

        if (response.data && response.data.data) {
          this.voyages.items.push.apply(this.voyages.items, response.data.data);
          this.voyages.apiTotalPages = response.data.max_page;
          parameters.page += 1;
        } else {
          break;
        }
        countdown--;
      } while (parameters.page <= response.data.max_page && countdown);
    },

    filterParametersByNames(filterParameters, requiredFields) {
      // removes redundant parameters
      // (keeps only requiredFields from filterParameters)
      return pickBy(filterParameters, (value, name) => {
        return requiredFields.includes(name);
      });
    },

    handlePopupReady(evt) {
      evt._source.on("popupopen", this.handlePopupOpen);
      evt._source.on("popupclose", this.handlePopupClose);
    },

    handlePopupOpen(evt) {
      this.hightlightedVesselIMO = getNested(evt, "popup.options.itemId", null);
      this.selectedVesselIMO = getNested(evt, "popup.options.itemId", null);
    },

    handlePopupClose() {
      this.hightlightedVesselIMO = null;
      this.selectedVesselIMO = null;
    },

    async handleVoyagesExport(format) {
      this.voyages.exportLoading = true;
      const apiFilterValues = this.filterParametersByNames(
        this.filterParameters,
        this.requiredParameters.voyages
      );

      const response = await this.getVoyages({
        format,
        limit: 1000000,
        ...apiFilterValues
      });

      const timestamp = moment().format("YYYY-MM-DD-HH-mm");
      downloadAsFileByURL(
        getNested(response, "data.csv", null),
        `fleet-monitor-voyages-export_${timestamp}.${format}`
      );
      this.voyages.exportLoading = false;
    }
  },

  computed: {
    ...mapGetters("api", ["getActiveRequestsCount"]),

    originPortsFormatted() {
      return formatPorts(this.originsStat.ports).filter(item => !!item.coords);
    },

    destinationPortsFormatted() {
      return formatPorts(this.destinationsStat.ports).filter(
        item => !!item.coords
      );
    },

    mapBounds() {
      // calculates map bounds to display all objects on the map
      let coordinates = getNested(this, "vessels", [])
        .map(item => {
          const coords = getNested(item, "geometry.coordinates");
          if (coords) {
            return { lat: coords[1], lng: coords[0] };
          } else {
            return null;
          }
        })
        .filter(item => !!item);

      coordinates = coordinates.concat(
        this.originPortsFormatted.map(item => item.coords)
      );
      coordinates = coordinates.concat(
        this.destinationPortsFormatted.map(item => item.coords)
      );
      return getCoordinateBounds(coordinates);
    }
  }
};
</script>

<style scoped>
.fleet-card >>> .card-body {
  /* deep-scoped style, affets child components */
  min-height: 370px;
}

.fleet-map-card {
  height: 370px;
}

.segments-card >>> .chart-container {
  height: 360px;
}
</style>
